// File: components/GlobalTopBar.tsx

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  SearchIcon,
  UserPlus2Icon,
  ChevronDown,
  LogOut,
  Settings,
  KeyRound,
} from "lucide-react";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import SearchOverlay from "@/components/SearchOverlay";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";

export default function GlobalTopBar() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { can } = usePermission();
  const canAddPatient = can('patients:write');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowSearch(false);
        setShowDropdown(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "n" && canAddPatient) {
        e.preventDefault();
        setShowAddPatient(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canAddPatient]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout?.();
    setShowDropdown(false);
    navigate("/login");
  };

  // Show "Dr." title for anyone with a clinical profile (doctor_id)
  const isDoctor = !!currentUser?.doctor_id;

  const formatUserName = (name: string | undefined) => {
    if (!name) return isDoctor ? "Doctor" : "User";
    let formattedName = name;
    if (formattedName.toLowerCase().startsWith('dr. ')) {
      formattedName = formattedName.substring(4);
    } else if (formattedName.toLowerCase().startsWith('doctor ')) {
      formattedName = formattedName.substring(7);
    }
    return formattedName;
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "D";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 border-b border-border shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">

          {/* Brand */}
          <div className="flex items-center gap-2 min-w-0">
            <img src="/onecheckup-icon.png" alt="OneCheckUp" className="w-8 h-8 object-contain" />
            <div className="flex flex-col">
              <span className="text-sm font-bold text-foreground">OneCheckUp</span>
              <span className="text-[10px] text-muted-foreground -mt-0.5">EHR System</span>
            </div>
          </div>

          {/* Search — button that opens overlay, not a real input */}
          <div className="flex-1 max-w-2xl mx-4 sm:mx-6">
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="w-full flex items-center gap-2 h-9 px-3 text-sm bg-muted/50 hover:bg-muted/70 border border-transparent hover:border-border rounded-md transition-colors text-muted-foreground"
            >
              <SearchIcon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left hidden sm:block">Search patients, appointments...</span>
              <span className="flex-1 text-left sm:hidden">Search...</span>
              <kbd className="hidden lg:inline-flex h-5 select-none items-center gap-0.5 rounded border bg-background border-border px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile search */}
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden p-2 h-9 w-9"
              onClick={() => setShowSearch(true)}
            >
              <SearchIcon className="w-4 h-4" />
            </Button>

            {canAddPatient && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAddPatient(true)}
                className="font-medium px-3 sm:px-4 h-9"
              >
                <UserPlus2Icon className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline text-sm">New Patient</span>
                <span className="sm:hidden text-xs">New</span>
              </Button>
            )}

            <ThemeToggle />

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="ghost"
                className="flex items-center gap-2 hover:bg-accent p-2 h-9"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <div className="relative">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold">
                    {getInitials(currentUser?.name)}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-success rounded-full border-2 border-background" />
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-foreground">
                    {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                  </div>
                  <div className="text-[11px] text-muted-foreground capitalize">
                    {currentUser?.role || (isDoctor ? "Doctor" : "User")}
                  </div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden md:block" />
              </Button>

              {showDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  {/* Dropdown header — plain, no gradient */}
                  <div className="p-3 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                        {getInitials(currentUser?.name)}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-popover-foreground">
                          {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {currentUser?.email || ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="py-1">
                    {(isDoctor || currentUser?.role === 'admin') && (
                      <>
                        <button
                          className="flex items-center w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors"
                          onClick={() => { navigate("/settings"); setShowDropdown(false); }}
                        >
                          <Settings className="w-4 h-4 mr-3 text-muted-foreground" />
                          <div>
                            <div className="font-medium">Settings</div>
                            <div className="text-xs text-muted-foreground">Preferences and configuration</div>
                          </div>
                        </button>
                        <div className="my-1 border-t border-border" />
                      </>
                    )}
                    <button
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors"
                      onClick={() => { navigate("/change-password"); setShowDropdown(false); }}
                    >
                      <KeyRound className="w-4 h-4 mr-3 text-muted-foreground" />
                      <div>
                        <div className="font-medium">Change Password</div>
                        <div className="text-xs text-muted-foreground">Update your account password</div>
                      </div>
                    </button>
                    <div className="my-1 border-t border-border" />
                    <button
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      <div>
                        <div className="font-medium">Log out</div>
                        <div className="text-xs text-muted-foreground">Sign out of your account</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <NewPatientModal
        isOpen={showAddPatient}
        onClose={() => setShowAddPatient(false)}
        onPatientCreated={() => {
          setShowAddPatient(false);
          window.dispatchEvent(new CustomEvent('patient:created'));
        }}
      />
      <SearchOverlay open={showSearch} onClose={() => setShowSearch(false)} />
    </>
  );
}
